/**
 * Single source of truth for stripping legacy inline overspec / cost-savings
 * blocks out of requirements text.
 *
 * Older flows appended a trailing block to the salient-characteristics text
 * like:
 *
 *   ...requirements text...
 *
 *   REMOVED OVERSPECIFICATIONS:
 *   - ...
 *
 *   ESTIMATED COST SAVINGS:
 *   ...
 *
 * Those structured details now live in their own UI elements (and structured
 * API fields), so the editor / email body must show only the clean
 * requirements text. This helper trims everything from the first
 * `REMOVED OVERSPECIFICATIONS` or `ESTIMATED COST SAVINGS` marker onward
 * (case-insensitive). Clean text is returned unchanged.
 */
export function stripInlineOverspecs(text: string): string {
  if (!text) return text;

  const markers = ['REMOVED OVERSPECIFICATIONS', 'ESTIMATED COST SAVINGS'];
  const upper = text.toUpperCase();

  let firstIndex = -1;
  for (const marker of markers) {
    const idx = upper.indexOf(marker);
    if (idx !== -1 && (firstIndex === -1 || idx < firstIndex)) {
      firstIndex = idx;
    }
  }

  if (firstIndex === -1) return text;

  return text.slice(0, firstIndex).trimEnd();
}

/**
 * Recover the removed-overspec items from a legacy inline
 * `REMOVED OVERSPECIFICATIONS:` block embedded in requirements text.
 *
 * Older flows (and any cached UI payload generated before the structured
 * `removed_specs` field was surfaced) only carry the overspecs as a trailing
 * hyphen-bulleted block inside `minimum_requirements`. When the structured
 * field is absent we parse the inline block so the UI can still render them.
 *
 * Returns the bullet items (without the leading hyphen) as a string array.
 * Stops at the `ESTIMATED COST SAVINGS` marker if present. Returns [] when no
 * inline block exists.
 */
export function parseInlineOverspecs(text: string): string[] {
  if (!text) return [];

  const upper = text.toUpperCase();
  const start = upper.indexOf('REMOVED OVERSPECIFICATIONS');
  if (start === -1) return [];

  // Slice from just after the marker line to the cost-savings marker (if any).
  let block = text.slice(start);
  const costIdx = block.toUpperCase().indexOf('ESTIMATED COST SAVINGS');
  if (costIdx !== -1) block = block.slice(0, costIdx);

  return block
    .split('\n')
    .slice(1) // drop the "REMOVED OVERSPECIFICATIONS:" header line
    .map((line) => line.replace(/^\s*[-*]\s*/, '').trim())
    .filter((line) => line.length > 0);
}

export default stripInlineOverspecs;
